// application/authorizationUnitOfWork.js
import { AUTHORIZATION_INTENT } from './authorizationChange';
import { createAuthorization, validateAuthorization } from '../domain/model/Authorization';

/**
 * The staged working set for one client's authorizations.
 *
 * STAGED ON SAVE, matching the coverage tab beside it: two adjacent tabs where Cancel discards one
 * and keeps the other is a difference a user feels immediately. It also lets a letter be recorded
 * for a client that does not exist yet, with the flush stamping the id after the create.
 *
 * MUCH SIMPLER THAN THE COVERAGE UNIT OF WORK, which is the model showing through rather than
 * corners cut. Coverages carry an ORDER, so that one tracks a baseline, closes position gaps and
 * detects reordering. Letters have no order and no relationship to each other, so the staged set is
 * a plain list and the verbs are the plain three.
 *
 * @typedef {import('../domain/model/Authorization.types').Authorization} Authorization
 * @typedef {import('./authorizationChange').AuthorizationChange} AuthorizationChange
 */

/**
 * @typedef {Object} AuthorizationUnitOfWork
 * @property {(loaded: Authorization[]) => void} load - hydrate, keeping edits already staged
 * @property {() => Authorization[]} all - the live working set, tombstones excluded
 * @property {() => boolean} hasPendingChanges
 * @property {(attrs: Object) => Authorization} add - stages a new authorization from flat form
 *   attributes, minting its id and any limit ids the draft has not got
 * @property {(authorizationId: string, attrs: Object) => Authorization|null} update - restates a
 *   recorded authorization from flat form attributes, keeping its identity and its limit ids. NULL
 *   when nothing in the working set carries that id, which is a caller fault rather than a state to
 *   handle — the id came from a row this set published.
 * @property {(authorizationId: string) => void} remove
 * @property {(attrs: Object) => Array<{ code: string, limitIndex: number|null, capIndex: number|null }>}
 *   validate - the domain's verdict on a draft, before it is staged
 * @property {() => AuthorizationChange[]} changes - the staged intents, ready to flush
 * @property {() => void} markCommitted - clears the intent log after a successful flush
 */

/**
 * @param {Object} deps
 * @param {() => string} deps.newId - an id for a staged authorization; it becomes the document id
 *   on flush, so a staged record keeps one identity for its whole life.
 * @param {() => string} deps.newLimitId - an id for a limit inside one. Separate because a limit id
 *   is LOCAL to its aggregate and is never resolved as a document id.
 * @returns {AuthorizationUnitOfWork}
 */
// eslint-disable-next-line import/prefer-default-export
export function createAuthorizationUnitOfWork({ newId, newLimitId }) {
  /** @type {Authorization[]} */
  let authorizations = [];
  /**
   * Authorization id → the intent staged against it. A Map rather than a flag on the record, so the
   * working set stays plain aggregates and a create followed by a remove can collapse.
   *
   * @type {Map<string, string>}
   */
  const intents = new Map();

  /**
   * Gives every limit an id it does not already have. Minted here rather than in the domain factory,
   * which preserves whatever it is handed — minting on read would give a stored limit missing its id
   * a fresh value each time, and two reads of one document would disagree about which is which.
   *
   * A non-list reads as empty, matching the domain's own normalization: this runs BEFORE the factory
   * on every path, so throwing here would put a hand-edited document beyond the tolerance the
   * aggregate was given.
   */
  const withLimitIds = (limits) => {
    return (Array.isArray(limits) ? limits : []).map((limit) => ({
      ...limit,
      limitId: limit?.limitId ?? newLimitId(),
    }));
  };

  const find = (authorizationId) => {
    return authorizations.find((entry) => entry.authorizationId === authorizationId) ?? null;
  };

  const replace = (updated) => {
    authorizations = authorizations.map((entry) => {
      return entry.authorizationId === updated.authorizationId ? updated : entry;
    });
  };

  return {
    /**
     * Hydrates from storage WITHOUT discarding what is already staged — a tab panel unmounts on
     * switch, so this can run again while a biller has unsaved work. Anything carrying an intent
     * wins over the loaded copy, which is what makes a staged removal survive a reload.
     */
    load(loaded) {
      const staged = authorizations.filter((entry) => intents.has(entry.authorizationId));
      const stagedIds = new Set(staged.map((entry) => entry.authorizationId));

      authorizations = [
        ...staged,
        ...(loaded ?? []).filter((entry) => !stagedIds.has(entry.authorizationId)),
      ];
    },

    all() {
      return authorizations.filter((entry) => entry.isDeleted !== true);
    },

    hasPendingChanges() {
      return intents.size > 0;
    },

    validate(attrs) {
      return validateAuthorization({ ...attrs, limits: withLimitIds(attrs?.limits) });
    },

    add(attrs) {
      const authorization = createAuthorization({
        ...attrs,
        authorizationId: newId(),
        limits: withLimitIds(attrs?.limits),
      });

      authorizations = [...authorizations, authorization];
      intents.set(authorization.authorizationId, AUTHORIZATION_INTENT.CREATE);

      return authorization;
    },

    /**
     * Restates a recorded authorization, rebuilt through the factory exactly as `add` is so the two
     * cannot drift into producing differently-shaped aggregates.
     *
     * A CREATE INTENT SURVIVES AN EDIT rather than being promoted: a record staged this session has
     * not been written, so overwriting the intent would emit an update against a document that does
     * not exist yet.
     */
    update(authorizationId, attrs) {
      const existing = find(authorizationId);

      if (existing === null) {
        return null;
      }

      const updated = createAuthorization({
        ...attrs,
        authorizationId,
        clientId: existing.clientId,
        clinicId: existing.clinicId,
        limits: withLimitIds(attrs?.limits),
      });

      replace(updated);

      if (intents.get(authorizationId) !== AUTHORIZATION_INTENT.CREATE) {
        intents.set(authorizationId, AUTHORIZATION_INTENT.UPDATE);
      }

      return updated;
    },

    /**
     * Stages a removal. A record staged and removed in one session never reached Firestore, so it is
     * dropped outright — writing a tombstone would create a row solely to mark it deleted.
     */
    remove(authorizationId) {
      const existing = find(authorizationId);

      if (existing === null) {
        return;
      }

      if (intents.get(authorizationId) === AUTHORIZATION_INTENT.CREATE) {
        authorizations = authorizations.filter((entry) => entry.authorizationId !== authorizationId);
        intents.delete(authorizationId);
        return;
      }

      replace({ ...existing, isDeleted: true });
      intents.set(authorizationId, AUTHORIZATION_INTENT.DELETE);
    },

    changes() {
      return [...intents.entries()].reduce((staged, [authorizationId, intent]) => {
        const authorization = find(authorizationId);

        return authorization === null
          ? staged
          : staged.concat([{ intent, authorization }]);
      }, []);
    },

    markCommitted() {
      intents.clear();
      authorizations = authorizations.filter((entry) => entry.isDeleted !== true);
    },
  };
} // createAuthorizationUnitOfWork()
